import { Helmet } from "react-helmet-async";
import { useRef, useState, useMemo } from "react";
import { useSelector } from "react-redux";
// @mui
import {
  Card,
  Grid,
  Stack,
  Button,
  MenuItem,
  Container,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
} from "@mui/material";
import { Link as LinkIcon } from "@mui/icons-material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { useForm } from "react-hook-form";
import {
  useDeleteDeviceMutation,
  useGetDevicesQuery,
  useGetEventPlacesQuery,
  useLazyGetDevicesQuery,
} from "../redux/services/device.service";
import useDownloadAll from "../hooks/useDownloadAll";

// components
import Iconify from "../components/iconify";
import Scrollbar from "../components/scrollbar";
import Protected from "../protected/Protected";
// sections
import DeviceForm from "../sections/@dashboard/device/device.form";
import CustomTable from "../components/data-table/CustomDataTable";
import { selectAbility } from "../redux/slices/casl.slice";
import CustomDeleteConfirmation from "../components/dialog/CustomDeleteConfirmation";
import GenerateCodeForm from "../sections/@dashboard/device/generateDistributorCode.form";
import LinkDevicesForm from "../sections/@dashboard/subscription/deviceSubscription.form";
import { useGetOrganizationsQuery } from "../redux/services/organization.service";
import useResponsive from "../hooks/useResponsive";

export default function DevicePage() {
  const ability = useSelector(selectAbility);

  const { handleDownloadAll, isDownloading } = useDownloadAll(
    useLazyGetDevicesQuery,
    "Devices",
  );

  const [openDeviceForm, setOpenDeviceForm] = useState(false);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const [openGenerateCodeForm, setOpenGenerateCodeForm] = useState(false);
  const [openLinkDevicesForm, setOpenLinkDevicesForm] = useState(false);

  const [openRow, setOpenRow] = useState(null);

  const [deleteDevice, { isLoading: isDeleting }] = useDeleteDeviceMutation();

  const [selectedEventPlace, setSelectedEventPlace] = useState("");
  const [filteredTags, setFilteredTags] = useState([]);

  const downloadRef2 = useRef(null);

  const isTablet = useResponsive("down", "lg");

  const handleDownloadCodes = (devices) => {
    const data = devices.map((code) => `${code},`);

    const textContent = data.join("\n");

    const blob = new Blob([textContent], { type: "text/plain" });

    const url = URL.createObjectURL(blob);

    downloadRef2.current.href = url;
    downloadRef2.current.click();

    URL.revokeObjectURL(url);
  };

  const {
    isLoading: isOrgLoading,
    isFetching: isOrgFetching,
    data: organizations,
  } = useGetOrganizationsQuery(null, {
    // skip: user?.role?.name !== "admin",
  });

  const {
    data: eventPlaces,
    isSuccess: isSuccessEventPlaces,
    isLoading: isLoadingEventPlaces,
  } = useGetEventPlacesQuery({
    organizationId: localStorage.getItem("organizationId"),
  });

  const {
    register,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      organizationId: "",
      distributorId: "",
    },
  });

  const handleResetFilter = () => {
    setValue("organizationId", "");
    setSelectedEventPlace("");
    setValue("tag", "");
    setValue("deployDates", []);
    setFilteredTags([]);
  };

  const watchedOrgId = watch("organizationId");
  const watchedTag = watch("tag");

  const queryParams = useMemo(() => {
    const query = {};
    if (watchedOrgId) query.organizationId = watchedOrgId;
    if (selectedEventPlace) query.eventPlace = selectedEventPlace;
    if (watchedTag) query.tag = watchedTag;
    return query;
  }, [watchedOrgId, selectedEventPlace, watchedTag]);

  const handleEventPlaceChange = (event) => {
    try {
      const selectedPlace = event.target.value;
      setSelectedEventPlace(selectedPlace);

      // Filter tags based on selected event place
      const selectedTags =
        (eventPlaces?.data?.allEventPlaces || []).filter(
          (place) => place.eventPlace === selectedPlace && place.tags !== null,
        )[0]?.tags || [];

      setFilteredTags(selectedTags);

      // Reset the tag field (clear the value)
      setValue("tag", ""); // This will clear the tag field
    } catch (error) {
      // Catch and log any errors
      console.error("Error while handling event place change:", error);
    }
  };

  console.log(filteredTags);

  return (
    <Protected>
      <Helmet>
        <title> Devices </title>
      </Helmet>

      <Container>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Device
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "flex-end",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Button
              variant="outlined"
              startIcon={
                isDownloading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Iconify icon="eva:download-fill" />
                )
              }
              onClick={() =>
                handleDownloadAll({
                  organizationId: watch("organizationId"),
                  eventPlace: selectedEventPlace,
                  tag: watch("tag"),
                })
              }
              disabled={isDownloading}
            >
              Download All
            </Button>
            {ability.can("post", "Device") && (
              <Button
                variant="contained"
                onClick={setOpenDeviceForm}
                startIcon={<Iconify icon="eva:plus-fill" />}
              >
                New Devices
              </Button>
            )}
            {ability.can("Is", "admin") && (
              <Button
                variant="contained"
                onClick={() => setOpenLinkDevicesForm(true)}
                startIcon={<LinkIcon />}
              >
                Link Devices
              </Button>
            )}
            {ability.can("post", "Generate-Distributor-Device-Code") && (
              <Button
                variant="contained"
                onClick={() => {
                  setOpenGenerateCodeForm(true);
                }}
                startIcon={<Iconify icon="eva:plus-fill" />}
              >
                Generate Device Code
              </Button>
            )}
          </Box>

          {(ability.can("post", "Device") ||
            ability.can("patch", "Device")) && (
            <DeviceForm
              open={openDeviceForm}
              onClose={() => {
                setOpenDeviceForm(false);
              }}
              onDownload={handleDownloadCodes}
            />
          )}

          {ability.can("post", "Generate-Distributor-Device-Code") && (
            <GenerateCodeForm
              open={openGenerateCodeForm}
              onClose={() => setOpenGenerateCodeForm(false)}
              edit={false}
              onDownload={handleDownloadCodes}
            />
          )}
        </Stack>

        <Card>
          <a
            ref={downloadRef2}
            style={{ display: "none" }}
            download="devices.txt"
          >
            a
          </a>

          <Scrollbar>
            <CustomTable
              canSelect
              useDataQuery={useGetDevicesQuery}
              defaultQueryParams={queryParams}
              tableName="Device"
              searchLabel="Search Device..."
              searchCols={["code"]}
              resetParentForm={handleResetFilter}
              actions={
                ability.can("delete", "Device")
                  ? {
                      field: "actions",
                      headerName: "Action",
                      type: "actions",
                      getActions: ({ row }) =>
                        [
                          <GridActionsCellItem
                            key="delete"
                            sx={{
                              color: "error.main",
                            }}
                            icon={
                              <Iconify
                                color="error.main"
                                icon="eva:trash-2-outline"
                              />
                            }
                            label="Delete"
                            showInMenu
                            onClick={() => {
                              setOpenRow(row);
                              setOpenDeleteConfirmation(true);
                            }}
                          />,
                        ].filter((action) => action !== null),
                    }
                  : null
              }
              extraHeaderField={
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    mt: 1,
                  }}
                >
                  <Grid container spacing={1} flexBasis="100%">
                    {/* Organization Select */}
                    {ability.can("get", "Organizations") && (
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth>
                          <InputLabel
                            id="org-select-label"
                            error={Boolean(errors.organizationId)}
                          >
                            Organization
                          </InputLabel>
                          <Select
                            {...register("organizationId", { required: true })}
                            labelId="org-select-label"
                            id="demo-simple-select"
                            inputProps={{ "data-testid": "organizationId" }}
                            label="Organization"
                            error={Boolean(errors.organizationId)}
                            value={watch("organizationId")}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {isOrgLoading || isOrgFetching ? (
                              <MenuItem
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  width: "100%",
                                }}
                              >
                                <CircularProgress size={20} />
                              </MenuItem>
                            ) : (
                              organizations?.data
                                ?.toSorted((a, b) =>
                                  a?.name?.localeCompare(b?.name),
                                )
                                ?.map((org) => (
                                  <MenuItem key={org?.id} value={org?.id}>
                                    {org?.name}
                                  </MenuItem>
                                ))
                            )}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}

                    {/* Event Place Select */}
                    {ability.can("get", "All-Event-Place") && (
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth>
                          <InputLabel id="eventPlace-label">
                            Event Place
                          </InputLabel>
                          <Select
                            labelId="eventPlace-label"
                            id="demo-simple-select"
                            {...register("eventPlace")}
                            label="Event Place"
                            value={selectedEventPlace}
                            onChange={handleEventPlaceChange}
                            inputProps={{ "data-testid": "eventPlace" }}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {isLoadingEventPlaces && (
                              <MenuItem>
                                <CircularProgress size={24} />
                              </MenuItem>
                            )}
                            {isSuccessEventPlaces &&
                              eventPlaces?.data?.allEventPlaces
                                ?.filter((eventPlace) => eventPlace?.eventPlace && typeof eventPlace.eventPlace === "string" && eventPlace.eventPlace.trim() !== "")
                                ?.map(
                                  (eventPlace, index) => (
                                    <MenuItem
                                      key={`${eventPlace.eventPlace}-${index}`}
                                      value={eventPlace.eventPlace}
                                    >
                                      {eventPlace.eventPlace}
                                    </MenuItem>
                                  ),
                                )}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}

                    {/* Tag Select */}
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth>
                        <InputLabel id="tag-label">Tag</InputLabel>
                        <Select
                          labelId="tag-label"
                          id="tag-select"
                          label="Tag"
                          value={watch("tag") ?? ""}
                          onChange={(e) => setValue("tag", e.target.value)}
                          disabled={filteredTags.length === 0}
                          inputProps={{ "data-testid": "filterTag" }}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {filteredTags.length > 0 ? (
                            filteredTags.map((tag, index) => (
                              <MenuItem key={index} value={tag}>
                                {tag}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem disabled>No tags available</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              }
            />
          </Scrollbar>
        </Card>
      </Container>

      {ability.can("delete", "Device") && (
        <CustomDeleteConfirmation
          open={openDeleteConfirmation}
          onClose={() => setOpenDeleteConfirmation(false)}
          tableName="Device"
          deleteFunc={deleteDevice}
          nameKey="code"
          row={openRow}
          isLoading={isDeleting}
        />
      )}

      <LinkDevicesForm
        open={openLinkDevicesForm}
        onClose={() => setOpenLinkDevicesForm(false)}
      />
    </Protected>
  );
}